import React from "react";
import { Link } from "react-router-dom";
import { FaFileContract, FaInfoCircle, FaGavel } from "react-icons/fa";

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block p-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full mb-4">
            <FaFileContract className="text-4xl text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-600">
            Legal terms governing the use of Sea Pulse Albania services
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Effective Date: January 1, 2025 | Last Updated: January 2025
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
          {/* Introduction */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <FaInfoCircle className="text-2xl text-cyan-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                1. Introduction
              </h2>
            </div>
            <p className="text-gray-700 mb-4">
              Welcome to Sea Pulse Albania ("Company," "we," "our," "us"). These
              Terms and Conditions ("Terms") govern your access to and use of
              our website www.seapulsealbania.com and any services provided by
              Sea Pulse Albania, including but not limited to boat tours,
              rentals, and related activities.
            </p>
            <p className="text-gray-700">
              By accessing our website or booking any service, you agree to be
              bound by these Terms. If you do not agree with any part of these
              Terms, please do not use our services.
            </p>
          </div>

          {/* Company Information */}
          <div className="mb-12 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Company Information
            </h3>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Business Name:</strong> Sea Pulse Albania
              </p>
              <p>
                <strong>Location:</strong> Durrës Marina, Durrës, Albania
              </p>
              <p>
                <strong>Email:</strong> seapulsealbania@gmail.com
              </p>
              <p>
                <strong>Phone:</strong> +355 69 205 4408
              </p>
              <p>
                <strong>Business Type:</strong> Professional Maritime Tourism
                Services
              </p>
            </div>
          </div>

          {/* Service Descriptions */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              2. Services Provided
            </h2>
            <p className="text-gray-700 mb-4">
              Sea Pulse Albania provides the following maritime tourism
              services:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Guided boat tours along the Albanian Riviera</li>
              <li>Boat rentals with professional skipper services</li>
              <li>Water sports activities and equipment rental</li>
              <li>Customized private boat experiences</li>
              <li>Group and corporate event packages</li>
            </ul>
          </div>

          {/* Booking & Reservations */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              3. Booking & Reservations
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  3.1 Booking Confirmation
                </h3>
                <p className="text-gray-700">
                  A booking is confirmed only upon receipt of full payment and
                  issuance of a confirmation email. We reserve the right to
                  refuse any booking without providing a reason.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  3.2 Age Requirements
                </h3>
                <p className="text-gray-700">
                  The person making the booking must be at least 18 years old.
                  Minors must be accompanied by a responsible adult at all times
                  during the tour.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  3.3 Accurate Information
                </h3>
                <p className="text-gray-700">
                  You are responsible for providing accurate and complete
                  information during booking. This includes contact details,
                  number of participants, and any special requirements.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">3.4 Group Size</h3>
                <p className="text-gray-700">
                  Each boat has a maximum capacity for safety reasons. You must
                  not exceed the number of participants specified in your
                  booking confirmation.
                </p>
              </div>
            </div>
          </div>

          {/* Payment Terms */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              4. Payment Terms
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  4.1 Payment in Full
                </h3>
                <p className="text-gray-700">
                  Full payment is required at the time of booking to secure your
                  reservation.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">4.2 Pricing</h3>
                <p className="text-gray-700">
                  All prices are quoted in Euros (EUR) and are subject to change
                  without notice. The price applicable to your booking is the
                  price displayed at the time of payment.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  4.3 Additional Charges
                </h3>
                <p className="text-gray-700">
                  Any optional extras (food, drinks, equipment) must be paid for
                  separately. Damage to equipment or the vessel will be charged
                  at replacement cost.
                </p>
              </div>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              5. Cancellation & Modification
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Full details of our cancellation policy are available in our{" "}
                <Link
                  to="/payment-policy"
                  className="text-cyan-600 hover:underline font-medium"
                >
                  Payment Policy
                </Link>
                . Key points include:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>48+ hours notice: 100% refund</li>
                <li>24-48 hours notice: 50% refund</li>
                <li>Less than 24 hours: No refund</li>
                <li>
                  Weather cancellations by us: Full refund or free rescheduling
                </li>
              </ul>
            </div>
          </div>

          {/* Customer Responsibilities */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              6. Customer Responsibilities
            </h2>
            <div className="space-y-4">
              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  6.1 Safety Compliance
                </h3>
                <p className="text-gray-700">
                  You must comply with all safety instructions provided by our
                  skipper and crew. Failure to follow safety guidelines may
                  result in immediate termination of service without refund.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  6.2 Health & Fitness
                </h3>
                <p className="text-gray-700">
                  You must inform us of any medical conditions, disabilities, or
                  pregnancy that may affect your participation. We reserve the
                  right to refuse service if we believe participation poses a
                  health risk.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">6.3 Conduct</h3>
                <p className="text-gray-700">
                  Inappropriate behavior, intoxication, or actions that endanger
                  others will result in immediate termination of service without
                  refund. We reserve the right to refuse service to intoxicated
                  individuals.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  6.4 Punctuality
                </h3>
                <p className="text-gray-700">
                  You must arrive at least 15 minutes before the scheduled
                  departure time. Late arrivals may result in shortened tour
                  duration without price adjustment.
                </p>
              </div>
            </div>
          </div>

          {/* Liability & Insurance */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <FaGavel className="text-2xl text-cyan-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                7. Liability & Insurance
              </h2>
            </div>
            <div className="space-y-4">
              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  7.1 Our Insurance Coverage
                </h3>
                <p className="text-gray-700">
                  All our vessels are fully insured and operated by licensed
                  professionals. We maintain comprehensive liability insurance
                  as required by Albanian maritime law.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  7.2 Personal Belongings
                </h3>
                <p className="text-gray-700">
                  We are not responsible for loss, theft, or damage to personal
                  belongings. You are advised to secure valuables and consider
                  personal travel insurance.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  7.3 Limitation of Liability
                </h3>
                <p className="text-gray-700">
                  Our liability is limited to the amount paid for your booking.
                  We are not liable for indirect, consequential, or incidental
                  damages. This limitation does not affect your statutory rights
                  under Albanian consumer protection law.
                </p>
              </div>

              <div className="border-l-4 border-cyan-500 pl-6">
                <h3 className="font-bold text-gray-900 mb-2">
                  7.4 Assumption of Risk
                </h3>
                <p className="text-gray-700">
                  Maritime activities carry inherent risks. By participating,
                  you acknowledge and accept these risks. We strongly recommend
                  obtaining personal travel and accident insurance.
                </p>
              </div>
            </div>
          </div>

          {/* Weather & Force Majeure */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              8. Weather & Force Majeure
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                Safety is our top priority. We reserve the right to cancel,
                postpone, or modify any tour due to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Adverse weather conditions</li>
                <li>Sea state or water conditions deemed unsafe</li>
                <li>Mechanical failures or technical issues</li>
                <li>Government restrictions or port closures</li>
                <li>Any other circumstances beyond our reasonable control</li>
              </ul>
              <p className="text-gray-700 mt-4">
                In such cases, you will be offered a full refund or the option
                to reschedule to any available date at no additional charge.
              </p>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Intellectual Property
            </h2>
            <p className="text-gray-700 mb-4">
              All content on our website, including text, graphics, logos,
              images, and software, is the property of Sea Pulse Albania and
              protected by copyright and trademark laws.
            </p>
            <p className="text-gray-700">
              You may not reproduce, distribute, or create derivative works
              without our express written permission.
            </p>
          </div>

          {/* Photography & Media */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Photography & Media Release
            </h2>
            <p className="text-gray-700">
              During tours, we may take photographs or videos for promotional
              purposes. By participating, you consent to being photographed and
              grant us the right to use such images in our marketing materials.
              If you do not wish to be photographed, please inform our staff.
            </p>
          </div>

          {/* Privacy */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Privacy & Data Protection
            </h2>
            <p className="text-gray-700">
              We collect and process personal data in accordance with applicable
              data protection laws. Your information is used solely for booking
              purposes and will not be shared with third parties except as
              necessary to provide our services (e.g., payment processing).
            </p>
          </div>

          {/* Complaints & Disputes */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. Complaints & Dispute Resolution
            </h2>
            <div className="space-y-4">
              <p className="text-gray-700">
                If you have any complaints or concerns, please contact us
                immediately:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Email: seapulsealbania@gmail.com</li>
                <li>Phone: +355 69 205 4408</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We will make every effort to resolve complaints promptly and
                fairly. If we cannot reach a satisfactory resolution, disputes
                shall be governed by the laws of Albania and subject to the
                exclusive jurisdiction of Albanian courts.
              </p>
            </div>
          </div>

          {/* Amendments */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              13. Amendments to Terms
            </h2>
            <p className="text-gray-700">
              We reserve the right to modify these Terms at any time. Changes
              will be effective immediately upon posting to our website. Your
              continued use of our services constitutes acceptance of the
              modified Terms. We recommend reviewing these Terms periodically.
            </p>
          </div>

          {/* Severability */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              14. Severability
            </h2>
            <p className="text-gray-700">
              If any provision of these Terms is found to be invalid or
              unenforceable, the remaining provisions shall remain in full force
              and effect.
            </p>
          </div>

          {/* Entire Agreement */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              15. Entire Agreement
            </h2>
            <p className="text-gray-700">
              These Terms, together with our Payment Policy, constitute the
              entire agreement between you and Sea Pulse Albania regarding the
              use of our services and supersede all prior agreements and
              understandings.
            </p>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3">
              Questions About These Terms?
            </h3>
            <p className="text-gray-700 mb-4">
              If you have any questions or concerns about these Terms and
              Conditions, please don't hesitate to contact us.
            </p>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:seapulsealbania@gmail.com"
                  className="text-cyan-600 hover:underline"
                >
                  seapulsealbania@gmail.com
                </a>
              </p>
              <p>
                <strong>Phone:</strong>{" "}
                <a
                  href="tel:+355692054408"
                  className="text-cyan-600 hover:underline"
                >
                  +355 69 205 4408
                </a>
              </p>
              <p>
                <strong>Address:</strong> Durrës Marina, Durrës, Albania
              </p>
            </div>
          </div>
        </div>

        {/* Footer Navigation */}
        <div className="text-center mb-8">
          <p className="text-gray-700 mb-4 font-medium">
            By using our services, you acknowledge that you have read,
            understood, and agree to be bound by these Terms and Conditions.
          </p>
          <div className="flex justify-center gap-6 text-gray-600">
            <Link
              to="/boat-tours"
              className="hover:text-cyan-600 transition-colors"
            >
              Browse Tours
            </Link>
            <span>•</span>
            <Link
              to="/payment-policy"
              className="hover:text-cyan-600 transition-colors"
            >
              Payment Policy
            </Link>
            <span>•</span>
            <Link to="/about" className="hover:text-cyan-600 transition-colors">
              About Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
